import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import {
    useSensors,
    useSensor,
    PointerSensor,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    UniqueIdentifier,
    pointerWithin,
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";

import { SortableItem, DroppableItem, DragOverlayItem, DraggableListRow } from "../react/DraggableList";
import { classNames } from "../../lib/Util";
import { translate } from "../IntlProviderWrapper";
import SearchInput from "../react/SearchInput";
import ConfirmationModal from "../ConfirmationModal";
import {
    DEFAULT_LINE_SECTION_ID,
    toggleLineDivider,
    generateTempLineSectionId,
    moveLineBetweenSections,
    parseSectionId,
    updateLinesInSection,
} from "../../lib/lineLayoutEditor";

import type { LineSection, SectionLine, LineData, DraggableItem } from "../../lib/lineLayoutEditor";

import "./LineLayoutEditor.scss";

// ---------------------------------------------------------------------------------------------------------------------
// SectionItemContent
// ---------------------------------------------------------------------------------------------------------------------

type SectionItemContentProps = {
    section: LineSection;
    isEditing?: boolean;
    onTitleChange?: (section: LineSection, newTitle: string) => void;
    onEdit?: (section: LineSection) => void;
    onDelete?: (section: LineSection) => void;
};

const SectionItemContent = ({
    section,
    isEditing = false,
    onTitleChange,
    onEdit,
    onDelete,
}: SectionItemContentProps) => {
    const [editValue, setEditValue] = useState<string>(section.title);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [isEditing]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && onTitleChange) {
            onTitleChange(section, editValue);
        }
    };

    const handleBlur = () => {
        if (onTitleChange && editValue.trim() !== "") {
            onTitleChange(section, editValue);
        }
    };

    const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (onEdit) {
            onEdit(section);
        }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (onDelete) {
            onDelete(section);
        }
    };

    return (
        <div
            className={classNames("line-layout-editor__section-content", {
                "is-editing": isEditing,
            })}
        >
            {isEditing ? (
                <input
                    ref={inputRef}
                    type="text"
                    className="line-layout-editor__section-title-input form-control"
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
            ) : (
                <>
                    <span className="line-layout-editor__section-title">{section.title}</span>
                    {(onEdit || onDelete) && (
                        <div className="line-layout-editor__section-actions">
                            {onEdit && (
                                <button
                                    className="line-layout-editor__section-edit-button"
                                    onClick={handleEditClick}
                                    title={translate("LineLayoutEditor.edit_section_title", "Edit section title")}
                                >
                                    <i className="fas fa-edit" />
                                </button>
                            )}
                            {onDelete && (
                                <button
                                    className="line-layout-editor__section-delete-button"
                                    onClick={handleDeleteClick}
                                    title={translate("LineLayoutEditor.delete_section", "Delete section")}
                                >
                                    <i className="fas fa-trash-alt" />
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
            {section.lines && (
                <span
                    className="line-layout-editor__section-lines-count"
                    title={translate("LineLayoutEditor.number_of_lines", "Number of lines")}
                >
                    {section.lines.length}
                </span>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// LineItemContent
// ---------------------------------------------------------------------------------------------------------------------

type LineItemContentProps = {
    line: SectionLine;
    lineTitle: string;
    showToggle?: boolean;
    onDividerToggle?: (line: SectionLine) => void;
};

const LineItemContent = ({ line, lineTitle, showToggle = false, onDividerToggle }: LineItemContentProps) => {
    const handleDividerToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (onDividerToggle) {
            onDividerToggle(line);
        }
    };

    return (
        <div className="line-layout-editor__line-content">
            <span className="line-layout-editor__line-title">{lineTitle}</span>
            {showToggle && (
                <>
                    <button
                        className={classNames("line-layout-editor__line-divider-toggle btn btn-flat-light", {
                            "has-divider-after": line.showDividerAfter,
                        })}
                        onClick={handleDividerToggle}
                        title={
                            line.showDividerAfter
                                ? translate("LineLayoutEditor.remove_divider", "Remove divider")
                                : translate("LineLayoutEditor.add_divider", "Add divider")
                        }
                    >
                        <i
                            className={classNames("fas", {
                                "fa-plus": !line.showDividerAfter,
                                "fa-trash-alt": line.showDividerAfter,
                            })}
                        />
                        {translate("LineLayoutEditor.divider", "Divider")}
                    </button>
                    {line.showDividerAfter && <div className="line-layout-editor__line-divider" />}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// LineLayoutEditor
// ---------------------------------------------------------------------------------------------------------------------

export type LineLayoutEditorHandle = {
    getSections: () => LineSection[];
    isDragActive: () => boolean;
    cancelDrag: () => void;
};

type LineLayoutEditorProps = {
    sections: LineSection[];
};

const LineLayoutEditor = forwardRef<LineLayoutEditorHandle, LineLayoutEditorProps>((props, ref) => {
    const [sections, setSections] = useState<LineSection[]>(props.sections);
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const [showDroppables, setShowDroppables] = useState<boolean>(false);
    const [selectedSectionId, setSelectedSectionId] = useState<string | null>(props.sections[0]?.id ?? null);
    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    const [sectionToDelete, setSectionToDelete] = useState<LineSection | null>(null);
    const [sectionsFilter, setSectionsFilter] = useState<string>("");
    const [linesFilter, setLinesFilter] = useState<string>("");

    // Only pass Mouse & Pointer sensors to exclude the KeyboardSensor
    const sensors = useSensors(useSensor(PointerSensor));

    const allItems = useMemo(
        () =>
            [
                sections.map(item => ({ item, parent: null })),
                sections.map(item => item.lines.map(line => ({ item: line, parent: item }))).flat(),
            ].flat(),
        [sections]
    );

    const allItemsById: Record<string, { item: LineData; parent: SectionLine | null }> = useMemo(
        () => allItems.reduce((memo, item) => ({ ...memo, [item.item.id]: item }), {}),
        [allItems]
    );

    const getItemFromId = (id: UniqueIdentifier): DraggableItem => allItemsById[id].item;
    const getParentFromId = (id: UniqueIdentifier): SectionLine | null => allItemsById[id].parent;

    const isSection = (item: DraggableItem): item is LineSection => "lines" in item;
    const isLine = (item: DraggableItem): item is SectionLine => !isSection(item);

    const getActiveItem = () => (activeId !== null ? getItemFromId(activeId) : undefined);

    const filteredSections = useMemo(() => {
        if (sectionsFilter === "") {
            return sections;
        }

        const normalizedSectionsFilter = sectionsFilter.toLowerCase();
        return sections.filter(
            section =>
                section.id === DEFAULT_LINE_SECTION_ID || section.title.toLowerCase().includes(normalizedSectionsFilter)
        );
    }, [sections, sectionsFilter]);

    // Get the selected section and its lines
    const selectedSection = sections.find(s => s.id === selectedSectionId);
    const selectedSectionLines = selectedSection?.lines ?? [];

    // Filter lines for display
    const filteredLines = useMemo(() => {
        if (linesFilter === "") {
            return selectedSectionLines;
        }

        const normalizedLinesFilter = linesFilter.toLowerCase();
        return selectedSectionLines.filter(line => line.title.toLowerCase().includes(normalizedLinesFilter));
    }, [selectedSectionLines, linesFilter]);

    const cancelDrag = useCallback(() => {
        setShowDroppables(false);
        setActiveId(null);
    }, []);

    // Expose methods to parent via ref
    useImperativeHandle(
        ref,
        () => ({
            getSections: () => sections.filter(section => section.id !== DEFAULT_LINE_SECTION_ID),
            isDragActive: () => Boolean(activeId),
            cancelDrag,
        }),
        [sections, activeId, cancelDrag]
    );

    // Handle toggling divider for a line
    const handleDividerToggle = (line: SectionLine) => {
        if (selectedSectionId === null || selectedSectionId === DEFAULT_LINE_SECTION_ID) {
            return;
        }

        setSections(toggleLineDivider(sections, selectedSectionId, line.id));
    };

    // Handle adding a new section
    const handleSectionAdd = () => {
        const newSectionId = generateTempLineSectionId(sections);

        const newSection: LineSection = {
            id: newSectionId,
            title: `${translate("LineLayoutEditor.new_section", "New section")} ${Math.abs(parseSectionId(newSectionId))}`,
            lines: [],
        };

        setSections(
            [...sections, newSection].sort((a, b) =>
                // the default section is always the last
                DEFAULT_LINE_SECTION_ID === a.id ? 1 : DEFAULT_LINE_SECTION_ID === b.id ? -1 : 0
            )
        );
        setSelectedSectionId(newSectionId);
        setEditingSectionId(newSectionId);
    };

    // Handle section selection (clears lines filter)
    const handleSectionSelect = (section: LineSection) => {
        setSelectedSectionId(section.id);

        // Clear lines filter when switching sections
        setLinesFilter("");
    };

    // Handle updating section title
    const handleSectionTitleChange = (section: LineSection, newTitle: string) => {
        setSections(sections.map(s => (s.id === section.id ? { ...s, title: newTitle.trim() } : s)));
        setEditingSectionId(null);
    };

    // Handle editing a section
    const handleSectionEdit = (section: LineSection) => {
        setEditingSectionId(section.id);
    };

    // Handle deleting a section (show confirmation)
    const handleSectionDelete = (section: LineSection) => {
        setSectionToDelete(section);
    };

    // Handle confirmed deletion
    const handleDeleteConfirm = () => {
        if (!sectionToDelete) {
            return;
        }

        // Remove the section from the list (lines will automatically go to default section)
        const newSections = sections.filter(s => s.id !== sectionToDelete.id);
        // Add the lines from the deleted section to the default section
        const defaultSection = newSections.find(s => s.id === DEFAULT_LINE_SECTION_ID);
        if (defaultSection) {
            defaultSection.lines = [...defaultSection.lines, ...sectionToDelete.lines];
        }

        setSections(newSections);

        // If the deleted section was selected, select the default section
        if (selectedSectionId === sectionToDelete.id) {
            setSelectedSectionId(DEFAULT_LINE_SECTION_ID);
        }

        setSectionToDelete(null);
    };

    // Handle cancelled deletion
    const handleDeleteCancel = () => {
        setSectionToDelete(null);
    };

    const handleDragStart = (event: DragStartEvent) => {
        const isLine = event.active.data.current?.type === "line";
        setActiveId(event.active.id);
        setShowDroppables(isLine);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const activeData = active.data.current ?? {};
        const overData = over?.data.current ?? {};

        cancelDrag();

        // If the item is not dragged to a position, do nothing
        if (!over) return;
        // If the item is dragged to the same position, do nothing
        if (active.id === over.id) return;
        // If the section item is dragged to a line item, do nothing
        if (activeData.type === "section" && overData.type === "line") return;
        // Don't allow reordering sections onto the default section
        if (activeData.type === "section" && over.id === DEFAULT_LINE_SECTION_ID) return;

        // Dragging line to a section
        if (activeData.type === "line" && overData.droppable) {
            const line = getItemFromId(active.id) as SectionLine;
            const sourceSectionId = getParentFromId(line.id)!.id;
            const targetSectionId = overData.id;
            if (sourceSectionId === targetSectionId) {
                return;
            }

            setSections(moveLineBetweenSections(sections, sourceSectionId, targetSectionId, line));
            return;
        }

        // Sorting (lines and sections)
        const isSection = activeData.type === "section";

        // Don't allow reordering lines in the default section
        if (!isSection && getParentFromId(active.id)?.id === DEFAULT_LINE_SECTION_ID) return;

        // Don't allow sorting if some items are hidden by filters
        if (isSection && sectionsFilter !== "") return;
        if (!isSection && linesFilter !== "") return;

        const itemsToSort: DraggableItem[] = isSection ? filteredSections : filteredLines;
        const oldIndex = itemsToSort.findIndex(item => item.id === active.id);
        const newIndex = itemsToSort.findIndex(item => item.id === over.id);
        const sortedItems = arrayMove(itemsToSort, oldIndex, newIndex);

        setSections(sections =>
            isSection
                ? (sortedItems as LineSection[])
                : updateLinesInSection(sections, sortedItems as SectionLine[], selectedSectionId as string)
        );
    };

    const handleDragCancel = () => {
        cancelDrag();
    };

    const lineGripIcon = <i className="fas fa-grip-vertical" />;
    const sectionGripIcon = <i className="fas fa-sort" />;

    const renderLineRow = (line: SectionLine, options?: { isOverlay?: boolean }) => (
        <LineItemContent
            line={line}
            lineTitle={line.title}
            showToggle={!options?.isOverlay && selectedSectionId !== DEFAULT_LINE_SECTION_ID}
            onDividerToggle={options?.isOverlay ? undefined : handleDividerToggle}
        />
    );

    const renderSectionRow = (section: LineSection, options?: { isOverlay?: boolean }) => (
        <SectionItemContent
            section={section}
            isEditing={!options?.isOverlay && editingSectionId === section.id}
            onTitleChange={options?.isOverlay ? undefined : handleSectionTitleChange}
            onEdit={options?.isOverlay || section.id === DEFAULT_LINE_SECTION_ID ? undefined : handleSectionEdit}
            onDelete={options?.isOverlay || section.id === DEFAULT_LINE_SECTION_ID ? undefined : handleSectionDelete}
        />
    );

    const renderSortableLine = (line: SectionLine) => (
        <SortableItem id={line.id} type="line" Icon={lineGripIcon}>
            {renderLineRow(line)}
        </SortableItem>
    );

    const renderLineOverlay = (line: SectionLine) => (
        <DraggableListRow Icon={lineGripIcon}>{renderLineRow(line, { isOverlay: true })}</DraggableListRow>
    );

    const renderSortableSection = (section: LineSection) => (
        <SortableItem
            id={section.id}
            selected={section === selectedSection}
            type="section"
            selectable={true}
            isDisabled={() => section.id === DEFAULT_LINE_SECTION_ID}
            Icon={sectionGripIcon}
        >
            {renderSectionRow(section)}
        </SortableItem>
    );

    const renderSectionOverlay = (section: LineSection) => (
        <DraggableListRow
            Icon={sectionGripIcon}
            className={classNames({
                "draggable-list__item--selected": section === selectedSection,
                "draggable-list__item--selectable": true,
            })}
        >
            {renderSectionRow(section, { isOverlay: true })}
        </DraggableListRow>
    );

    const renderDroppableSection = (section: LineSection, selected: boolean) => (
        <DroppableItem id={section.id} selected={selected}>
            <SectionItemContent section={section} />
        </DroppableItem>
    );

    const renderSectionLines = (activeLine?: SectionLine) => {
        if (selectedSectionId === null) {
            return (
                <div className="line-layout-editor__empty">
                    {translate("LineLayoutEditor.select_section", "Select a section")}
                </div>
            );
        }

        if (filteredLines.length > 0) {
            return (
                <SortableContext items={filteredLines.map(item => item.id)}>
                    <ul className="draggable-list">
                        {filteredLines.map(line => (
                            <li key={line.id}>{renderSortableLine(line)}</li>
                        ))}
                    </ul>
                    {activeLine && <DragOverlayItem>{renderLineOverlay(activeLine)}</DragOverlayItem>}
                </SortableContext>
            );
        }

        if (linesFilter !== "" && selectedSectionLines.length > 0) {
            return (
                <div className="line-layout-editor__empty">
                    {translate("common.no_results_found", "No results found")}
                </div>
            );
        }

        return (
            <div className="line-layout-editor__empty">
                {translate("LineLayoutEditor.no_lines_in_section", "No lines in this section")}
            </div>
        );
    };

    const activeItem = getActiveItem();

    return (
        <div className="line-layout-editor">
            <div className="line-layout-editor__content">
                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    collisionDetection={pointerWithin}
                >
                    <div className="line-layout-editor__column">
                        <div className="line-layout-editor__column-header">
                            <div className="line-layout-editor__column-title">
                                <span>{translate("LineLayoutEditor.sections", "Sections")}</span>
                                <button
                                    className="line-layout-editor__add-button btn btn-flat-light"
                                    onClick={handleSectionAdd}
                                >
                                    <i className="fas fa-plus" />
                                    <span>{translate("LineLayoutEditor.add_section", "Add section")}</span>
                                </button>
                            </div>
                            <SearchInput
                                value={sectionsFilter}
                                onChange={setSectionsFilter}
                                placeholder={translate("LineLayoutEditor.search_sections", "Search sections")}
                                className="line-layout-editor__search"
                            />
                        </div>

                        <SortableContext items={filteredSections.map(section => section.id)}>
                            <ul
                                className={classNames("draggable-list", {
                                    "draggable-list--external-drag-active": showDroppables,
                                })}
                            >
                                {showDroppables
                                    ? filteredSections.map(section => (
                                          <li key={section.id}>
                                              {renderDroppableSection(section, section === selectedSection)}
                                          </li>
                                      ))
                                    : filteredSections.map(section => (
                                          <li onClick={() => handleSectionSelect(section)} key={section.id}>
                                              {renderSortableSection(section)}
                                          </li>
                                      ))}
                            </ul>
                            {activeItem && isSection(activeItem) && (
                                <DragOverlayItem>{renderSectionOverlay(activeItem)}</DragOverlayItem>
                            )}
                        </SortableContext>
                    </div>
                    <div className="line-layout-editor__column">
                        <div className="line-layout-editor__column-header">
                            <p className="line-layout-editor__column-title">
                                {selectedSection ? (
                                    <span>
                                        {translate("LineLayoutEditor.lines_in", "Lines in")}{" "}
                                        <strong>{selectedSection.title}</strong>
                                    </span>
                                ) : (
                                    translate("LineLayoutEditor.select_section", "Select a section")
                                )}
                            </p>
                            {selectedSectionId !== null && (
                                <SearchInput
                                    className="line-layout-editor__search"
                                    placeholder={translate("common.search_lines", "Search lines")}
                                    value={linesFilter}
                                    disabled={selectedSectionLines.length === 0}
                                    onChange={setLinesFilter}
                                />
                            )}
                        </div>
                        {renderSectionLines(activeItem && isLine(activeItem) ? activeItem : undefined)}
                    </div>
                </DndContext>
            </div>

            <ConfirmationModal
                show={sectionToDelete !== null}
                title={translate("LineLayoutEditor.delete_section", "Delete section")}
                accept_label={translate("common.delete", "Delete")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onAccept={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            >
                {sectionToDelete && (
                    <>
                        <p className="mb-0">
                            <FormattedMessage
                                id="LineLayoutEditor.are_you_sure_delete_section"
                                defaultMessage='Are you sure you want to delete section "{title}"?'
                                values={{ title: <strong>{sectionToDelete.title}</strong> }}
                            />
                        </p>
                        {sectionToDelete.lines.length > 0 && (
                            <>
                                <FormattedMessage
                                    tagName="p"
                                    id="LineLayoutEditor.all_lines_will_be_moved_to_other_section"
                                    defaultMessage='All lines will be moved to default section "{defaultSectionTitle}".'
                                    values={{
                                        defaultSectionTitle: (
                                            <FormattedMessage
                                                id="common.other"
                                                defaultMessage="Other"
                                                tagName="strong"
                                            />
                                        ),
                                    }}
                                />
                                <div className="alert alert-warning mb-0">
                                    <ul className="my-0 pl-3">
                                        {sectionToDelete.lines.map(line => (
                                            <li key={line.id}>{line.title}</li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </>
                )}
            </ConfirmationModal>
        </div>
    );
});

export default LineLayoutEditor;
